import { getLogger } from '@logtape/logtape';
import useSWR, { mutate as globalMutate } from 'swr';

import useConfig from '@/hooks/useConfig.ts';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { GetAppointmentsForTodayResponseDto } from '@/models/appointment/GetAppointmentsForTodayResponseDto.ts';

import { fetchAppointmentsByDateRange } from '@/services/appointmentService.ts';

import {
    enrichAppointmentsListWithIsEternoUser,
    mergeAppointments,
    removeCancelledAppointments,
} from '@/utils/appointmentUtils.ts';
import { filterAppointmentsByDateRange } from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { isDevEnvironment } from '@/utils/envUtils.ts';
import { multiplyAppointmentsForTesting } from '@/utils/testingUtils.ts';

const logger = getLogger(['hook', 'appointments-for-date-range']);

const APPOINTMENTS_RANGE_KEY_PREFIX = 'appointments-range';

export const revalidateAppointmentsRangeCache = (): void => {
    void globalMutate((key) => typeof key === 'string' && key.startsWith(APPOINTMENTS_RANGE_KEY_PREFIX));
};

interface AppointmentsForDateRangeOptions {
    show_cancelled?: boolean;
    show_cancelled_today?: boolean;
    is_active?: boolean;
}

interface AppointmentsRangeData {
    response: GetAppointmentsForTodayResponseDto;
    is_complete: boolean;
}

interface UseAppointmentsForDateRangeResult {
    appointments: DailyAppointment[] | undefined;
    is_loading: boolean;
    is_complete: boolean;
    mutate: () => void;
}

export const useAppointmentsForDateRange = (
    start_date: Date | undefined,
    end_date: Date | undefined,
    options: AppointmentsForDateRangeOptions = {}
): UseAppointmentsForDateRangeResult => {
    const { customer_id, instance_ids, location_key } = useConfig();
    const { show_cancelled = false, show_cancelled_today = false, is_active = true } = options;
    const cacheKey =
        instance_ids?.length && start_date && end_date
            ? `${APPOINTMENTS_RANGE_KEY_PREFIX}-${dayjs(start_date).format('YYYY-MM-DD')}-${dayjs(end_date).format('YYYY-MM-DD')}-${customer_id}-${instance_ids.join(',')}-${location_key}`
            : undefined;

    const fetchAppointments = async (
        instance_id: string,
        start_date_str: string,
        end_date_str: string
    ): Promise<GetAppointmentsForTodayResponseDto | undefined> => {
        try {
            const response = await fetchAppointmentsByDateRange(
                customer_id,
                instance_id,
                start_date_str,
                end_date_str,
                location_key
            );
            return response;
        } catch (error) {
            logger.error('Failed to fetch appointments for instance {instanceId}', {
                instanceId: instance_id,
                error: error,
            });
            return undefined;
        }
    };

    const fetcher = async (): Promise<AppointmentsRangeData> => {
        if (!start_date || !end_date) {
            throw new Error('Start date and end date are required');
        }

        if (dayjs(start_date).isAfter(dayjs(end_date))) {
            throw new Error('Start date must be before or equal to end date');
        }

        const diffMonths = dayjs(end_date).diff(dayjs(start_date), DayjsUnit.MONTH);
        if (diffMonths > 3) {
            throw new Error('Date range cannot exceed 3 months');
        }

        const startDateString = dayjs(start_date).format('YYYY-MM-DD');
        const endDateString = dayjs(end_date).format('YYYY-MM-DD');

        // Use Promise.allSettled for better error handling
        const promises = instance_ids.map((instance_id) =>
            fetchAppointments(instance_id, startDateString, endDateString)
        );

        const results = await Promise.allSettled(promises);
        const responses: GetAppointmentsForTodayResponseDto[] = results
            .filter(
                (result): result is PromiseFulfilledResult<GetAppointmentsForTodayResponseDto | undefined> =>
                    result.status === 'fulfilled' && result.value !== undefined
            )
            .map((result) => result.value!);

        let mergedAppointments = mergeAppointments(responses);

        // Filter appointments by date range
        mergedAppointments = filterAppointmentsByDateRange(mergedAppointments, startDateString, endDateString);

        // Enrich appointments
        enrichAppointmentsListWithIsEternoUser(mergedAppointments.data.active);
        enrichAppointmentsListWithIsEternoUser(mergedAppointments.data.inactive);

        return { response: mergedAppointments, is_complete: responses.length === instance_ids.length };
    };

    const { data, isLoading, mutate } = useSWR<AppointmentsRangeData>(is_active ? cacheKey : undefined, fetcher, {
        refreshInterval: 15000,
        revalidateOnFocus: true,
        revalidateIfStale: false,
        revalidateOnReconnect: true,
        dedupingInterval: 10000, // Dedupe requests within 10 seconds
    });

    // Process the results
    const processedAppointments = ((): DailyAppointment[] | undefined => {
        if (!data || !cacheKey) return undefined;

        const combinedAppointments: DailyAppointment[] = [...data.response.data.active, ...data.response.data.inactive];

        // Filter out cancelled appointments if needed
        const filteredAppointments = show_cancelled
            ? combinedAppointments
            : removeCancelledAppointments(combinedAppointments, show_cancelled_today);

        // Apply testing multiplication only in dev environment
        const finalAppointments =
            filteredAppointments && isDevEnvironment()
                ? multiplyAppointmentsForTesting(filteredAppointments)
                : filteredAppointments;

        return finalAppointments;
    })();

    return {
        appointments: processedAppointments,
        is_loading: isLoading,
        is_complete: data?.is_complete ?? false,
        mutate: mutate,
    };
};
